import React, { useState } from "react";
import { ShareApplicationPerson } from "@/types/ShareApplication";
import { PrimaryButton, ReadOnlyField } from "@ucl/library";
import { useUnlinkedAccount } from "../useUnlinkedAccount";
import { InviteEmailButton } from "../InviteEmailButton";
import { CopyUrlComponent } from "../CopyUrlComponent";
import "./UnlinkedAccountComponent.scss";

export interface UnlinkedAccountComponentProps {
  person: ShareApplicationPerson;
  financialApplicationId?: string;
}

export const UnlinkedAccountComponent: React.FC<UnlinkedAccountComponentProps> = ({
  person,
  financialApplicationId,
}) => {
  const { generateInviteLink, isGenerating, error } = useUnlinkedAccount();
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [inviteId, setInviteId] = useState<string | null>(null);

  const handleGenerateInviteLink = async () => {
    if (!financialApplicationId) {
      console.error("Financial application ID is required");
      return;
    }

    if (!person.isEmailValid) {
      console.error("Invalid email address");
      return;
    }

    try {
      const response = await generateInviteLink(financialApplicationId, person);
      setInviteLink(response.inviteLink);
      setInviteId(response.inviteId);
    } catch (err) {
      // Error is already handled in the hook
      console.error("Failed to generate invite link:", err);
    }
  };

  // Get tooltip text for generate button
  const getGenerateButtonTooltip = (): string | undefined => {
    if (!financialApplicationId) {
      return "Financial application ID is required";
    }
    if (!person.isEmailValid) {
      return "Invalid email address format";
    }
    return undefined;
  };

  // If there's a potential user ID, this component shouldn't be used
  // The parent component should handle this case
  if (person.potentialUserId) {
    return (
      <div className="unlinked-account-component">
        <h3>Share Application with {person.fullName}</h3>
        <p>
          This person has an existing account. Please use the link option to connect them to their
          existing account.
        </p>
      </div>
    );
  }

  // If there's already a valid invite link, show it
  if (person.validInviteLink) {
    return (
      <div className="unlinked-account-component">
        <h3>Share Application with {person.fullName}</h3>
        <p>
          An invite link has been created for this person. You can copy and share this link with
          them.
        </p>

        <div className="unlinked-account-component__email-section">
          <ReadOnlyField
            value={person.emailAddress}
            label="Email Address"
            description="The email address where the invite will be sent"
            minimal={false}
          />
        </div>

        <CopyUrlComponent
          url={person.validInviteLink}
          label="Invite Link"
          description="Copy this link to share with the person"
        />

        <div className="unlinked-account-component__send-email-section">
          <InviteEmailButton
            person={person}
            financialApplicationId={financialApplicationId}
            buttonText="Send Invite Email"
            isReSend={false}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="unlinked-account-component">
      <h3>Share Application with {person.fullName}</h3>
      <p>
        This person doesn't have an account yet. Generate an invite link to share the application
        with them.
      </p>

      <div className="unlinked-account-component__email-section">
        <ReadOnlyField
          value={person.emailAddress}
          label="Email Address"
          description="The email address where the invite will be sent"
          minimal={false}
        />
      </div>

      {error && (
        <div className="unlinked-account-component__error">
          <p>Error: {error}</p>
        </div>
      )}

      {inviteLink ? (
        <div className="unlinked-account-component__success">
          <h4>Invite Link Generated Successfully!</h4>
          <p>The person's status has been updated to "Invite Created".</p>
          <ReadOnlyField
            value={inviteLink}
            label="Invite Link"
            description="Copy this link to share with the person"
            minimal={false}
          />

          <div className="unlinked-account-component__send-email-section">
            <InviteEmailButton
              person={{
                ...person,
                validInviteId: inviteId || person.validInviteId,
              }}
              financialApplicationId={financialApplicationId}
              buttonText="Send Invite Email"
              isReSend={false}
            />
          </div>
        </div>
      ) : (
        <div className="unlinked-account-component__generate-section">
          <PrimaryButton
            text={isGenerating ? "Generating..." : "Generate Invite Link"}
            onClick={handleGenerateInviteLink}
            disabled={isGenerating || !financialApplicationId || !person.isEmailValid}
            title={getGenerateButtonTooltip()}
          />
        </div>
      )}
    </div>
  );
};

export default UnlinkedAccountComponent;
